import { type Dict, type Lang } from "@/lib/i18n";
import { formatDate } from "@/lib/format";
import type { Module } from "@/lib/lecturehub/types";

/**
 * Scheduled-publish helpers. A module with `status === "scheduled"` is visible
 * in public listings but with its content (video / body / lessons) withheld by
 * the backend until `publishAt`. The FE shows a "coming soon" state and —
 * crucially — suppresses any buy/enroll CTA, since there's nothing to unlock
 * yet (scheduled wins over the paywall). See `ScheduledModuleDetail` + the card
 * `ScheduledBadge`.
 *
 * The public scoping is contract, not assumption: `modules` and `module` force
 * `status ∈ { published, scheduled }` server-side (docs/synced/portal-api
 * 03-queries.md + 06-pagination-filters-ordering.md), confirmed against live
 * api1. No scheduled row has been authored yet, so the rendering path itself is
 * still untested end-to-end.
 */
export function isScheduled(m: Pick<Module, "status">): boolean {
  return m.status === "scheduled";
}

/**
 * Localized "available on <date>" label for a scheduled module's card/detail,
 * or null when there's no usable `publishAt` (fall back to the bare badge copy).
 */
export function scheduledDateLabel(
  t: Dict["detail"],
  publishAt: string | null | undefined,
  lang: Lang,
): string | null {
  if (!publishAt) return null;
  return `${t.scheduledOn} ${formatDate(publishAt, lang)}`;
}
